import React from "react";

import { useNotification, useTranslation } from "@refinedev/core";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Button, type ButtonProps, IconButton, type IconButtonProps } from "@mui/material";

interface CopyButtonProps {
  content: string;
  variant?: "button" | "icon";
  buttonText?: string;
  buttonProps?: ButtonProps;
  iconButtonProps?: Omit<IconButtonProps, "onClick">;
  "data-testid"?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  content,
  variant = "icon",
  buttonText,
  buttonProps,
  iconButtonProps,
  "data-testid": testId,
}) => {
  const { open: notify } = useNotification();
  const { translate } = useTranslation();

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(content);
      notify?.({
        type: "success",
        message: translate("common.clipboardCopy"),
      });
    } catch {
      notify?.({
        type: "error",
        message: translate("common.clipboardCopyError"),
      });
    }
  };

  if (variant === "button") {
    return (
      <Button onClick={handleClick} variant="text" {...buttonProps}>
        {buttonText}
      </Button>
    );
  }

  return (
    <IconButton size="small" color="primary" onClick={handleClick} data-testid={testId} {...iconButtonProps}>
      <ContentCopyIcon fontSize="small" />
    </IconButton>
  );
};
